import { Box, ContextualBanner, LogoLoader } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { Logo } from "components/logo/Logo";
import { PublicPassportResultCard } from "components/reports/passports/card/PublicPassportResultCard";
import style from "components/reports/passports/passports-view.scss";
import { passportService, PublicPassportReport } from "services/report/passports/PassportService";

import testIds from "testIds.json";

export const PassportSharedView = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const token = new URLSearchParams(location.search).get("token") ?? "";

    const [loading, setLoading] = React.useState(true);
    const [reports, setReports] = React.useState<PublicPassportReport[]>([]);
    const [errorMessage, setErrorMessage] = React.useState("");

    React.useEffect(() => {
        if (!token) {
            setErrorMessage(t("DevicePassport.shared.invalidLink"));
            setLoading(false);
            return;
        }
        const abortController = new AbortController();
        passportService
            .fetchSharedPassport(token, abortController)
            .then(async (response) => {
                if (response.language) {
                    await i18n.changeLanguage(response.language);
                }
                setReports(response.reports);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setErrorMessage(t("DevicePassport.shared.loadFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => abortController.abort();
    }, [token]);

    return (
        <Box columnLayout className={style.sharedContainer}>
            <Box justifyContent="SpaceBetween" verticalAlignment="Center" className={style.sharedBanner}>
                <Logo isWhiteLogo className={style.sharedBannerLogo} />
                <span className={style.sharedBannerTitle}>{t("Routes.devicePassport")}</span>
            </Box>
            <Box columnLayout className={style.sharedContent}>
                {loading && (
                    <div className={style.resultsArea}>
                        <LogoLoader testId={testIds.common.inProgressElement} />
                    </div>
                )}
                {!loading && errorMessage && (
                    <ContextualBanner variant="Error" testId={testIds.common.errorMessage.itself}>
                        {errorMessage}
                    </ContextualBanner>
                )}
                {!loading && !errorMessage && reports.length === 0 && (
                    <ContextualBanner variant="Info">{t("DevicePassport.shared.notFound")}</ContextualBanner>
                )}
                {!loading && reports.map((report, index) => <PublicPassportResultCard key={index} report={report} />)}
            </Box>
        </Box>
    );
};
